import {
  cloneElement,
  type CSSProperties,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  createContext,
  useMemo,
  type Ref,
} from 'react'
import cx from 'classnames'
import {
  m,
  type PanInfo,
  useDragControls,
  useMotionValue,
  useTransform,
  type Variants,
  type DragControls,
} from 'motion/react'
import { Dialog as AriaDialog, Heading, Modal } from 'react-aria-components'
import {
  ModalOverlay,
  ModalOverlayVariantNames,
} from '@/components/ModalOverlay'
import { useElementSize, useScrollOffsets } from '@/hooks/element-hooks'
import styles from './ModalDialog.module.css'
import { useModalStacking } from '@/components/ModalDialog/ModalStackContext'
import { modalSlideSpring, staticTransition } from '@/consts/springs'
import { isAncestorElement, mediaQueryMatchStream } from '@/utils/browser-util'
import { useObservable, useObservableValue } from '@/hooks/rx-hooks'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import { animate } from 'motion'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { getScrollParent } from '@react-aria/utils'
import type { Observable } from 'rxjs'
import { IconButton } from '@/components/IconButton'
import { ModalSearchField } from '@/components/ModalSearchField'
import { useScrollFadeMaskImage } from '@/hooks/scroll-fade'

const MotionDialog = m.create(AriaDialog)

// Special media query for whether
// there's enough space to use the floating style.
const hasSpaceToFloat$ = mediaQueryMatchStream(
  'screen and (min-height: 860px) and (min-width: 480px)',
)

const hasSpaceToFloatWide$ = mediaQueryMatchStream(
  'screen and (min-height: 700px) and (min-width: 1200px)',
)

export interface ModalDialogProps {
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  children?: ReactNode

  /**
   * Whether the Dialog is open.
   */
  isOpen: boolean

  /**
   * Called when the modal is requested to close.
   */
  onClose?: () => void

  /**
   * If specified, controls whether the modal is dismissable.
   * Default is `true`.
   */
  isDismissable?: boolean

  /**
   * Whether the modal can be dismissed by hitting Esc.
   */
  isKeyboardDismissDisabled?: boolean

  /**
   * Modal size.
   */
  size?: 'base' | 'lg' | 'wide'

  /**
   * When `true`, modal content will not be a scroll container; instead the
   * dialog will. Use this to increase the scroll area for forms.
   */
  isUsingDialogScrolling?: boolean

  /**
   * Whether drag gestures are disabled.
   * If not specified, then drag gestures are enabled,
   * unless {@link isUsingDialogScrolling} is enabled, in which case
   * this defaults to `true`.
   */
  isDraggingDisabled?: boolean

  /**
   * If specified, when the observable emits, we'll start a drag.
   */
  startDrag$?: Observable<PointerEvent>

  /**
   * An optional {@link ModalDialogHero} element.
   */
  hero?: ReactElement | null | undefined | false

  /**
   * Whether to use any gap between elements.
   */
  gap?: 'default' | 'none'

  /**
   * When rendering as a sheet, determines whether the sheet will
   * size itself based on the content, or if it'll stretch to fill
   * the available height.
   */
  sheetSizing?: 'auto' | 'stretch'

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Ignore outside interactions that land inside a different modal dialog.
 *
 * react-aria removes an overlay from its outside-click stack the moment it
 * starts closing, but its exit animation keeps that overlay mounted and on
 * top for a few hundred milliseconds. During that window a click on the
 * still-present closing modal is misattributed as an outside interaction on
 * the modal beneath it, dismissing it. Only genuine outside clicks (e.g. the
 * backdrop, which is not inside a dialog) should dismiss.
 */
function shouldCloseOnInteractOutsideDefault(element: Element) {
  return !element.closest(`.${styles.root}`)
}

const dialogTransitionVariants = {
  floating: {
    [ModalOverlayVariantNames.HIDDEN]: {
      opacity: 0,
      transform: `translateY(50px)`,
    },
    [ModalOverlayVariantNames.VISIBLE]: {
      opacity: 1,
      transform: `translateY(0px)`,
    },
  } as Variants,

  sheet: {
    [ModalOverlayVariantNames.HIDDEN]: {
      y: '100%',
    },
    [ModalOverlayVariantNames.VISIBLE]: {
      y: '0%',
    },
  } as Variants,
}

/**
 * Dialog root.
 */
export function ModalDialog({
  isOpen,
  onClose,
  children,
  isKeyboardDismissDisabled,
  hero,
  isUsingDialogScrolling = false,
  isDismissable = true,
  size = 'base',
  isDraggingDisabled,
  startDrag$,
  gap = 'default',
  sheetSizing = 'auto',
  testId,
  ...rest
}: ModalDialogProps) {
  isDraggingDisabled ??= isUsingDialogScrolling
  const dragControls = useDragControls()
  const isWideFloating = useObservableValue(hasSpaceToFloatWide$)
  const isStandardFloating = useObservableValue(hasSpaceToFloat$)
  const isFloating =
    size === 'wide' ? isWideFloating || isStandardFloating : isStandardFloating
  const effectiveSize = size === 'wide' && !isWideFloating ? 'lg' : size
  const { stackingTransform } = useModalStacking({
    isOpen,
    isSheet: !isFloating,
  })

  const allowSwipeDismissWhenOverflowingRef = useRef(false)
  useObservable(startDrag$, (e) => {
    allowSwipeDismissWhenOverflowingRef.current = true
    dragControls.start(e)
  })

  const openChange = useCallback(
    (open: boolean) => !open && onClose?.(),
    [onClose],
  )

  const dialogContainerRef = useRef<HTMLDivElement>(null)
  const { size: dialogSize, connectRef } = useElementSize()
  const composedDialogContainerRef = useComposedRefs(
    dialogContainerRef,
    connectRef,
  )

  // Tracks the `y` transform for the inner dialog based on
  // both dragging and enter/exit animations.
  // When dragging, the value will be a `number` representing pixels,
  // and running enter/exit animations, the value will be a string
  // representing a percentage, e.g. `20%`.
  const dialogY = useMotionValue<number | string>(0)

  const dialogAnimationVariants = isFloating
    ? dialogTransitionVariants.floating
    : dialogTransitionVariants.sheet

  // Handle drag end so we can dismiss the dialog when dragged far or fast
  // enough.
  const dragEnd = useCallback(
    (
      e: MouseEvent | TouchEvent | PointerEvent,
      { offset, velocity }: PanInfo,
    ) => {
      // We're not setting the ref value in the pointer-down handler
      // in this component since it'll always be triggered even when
      const allowSwipeDismissWhenOverflowing =
        allowSwipeDismissWhenOverflowingRef.current

      // Reset the flag after handling a drag.
      allowSwipeDismissWhenOverflowingRef.current = false

      // Close the dialog if dragged far or fast enough.
      if (!dialogContainerRef.current) {
        return
      }

      // How fast we need to drag to close the dialog.
      const velocityForClose = 300

      // Check if the nearest currently scrollable content is within
      // the dialog; if it is, and it's overflowing, don't dismiss.
      let dismissableViaSwipe = true
      if (e.target) {
        const scrollingParent = getScrollParent(e.target as Element, true)

        if (isAncestorElement(dialogContainerRef.current, scrollingParent)) {
          // If the drag was triggered externally (as in, a child component
          // emitted a value on the observable), then we'll allow the dismiss
          // since that component probably knows what its doing.
          if (!allowSwipeDismissWhenOverflowing) {
            dismissableViaSwipe = false
          }
        }
      }

      if (
        offset.y > dialogSize.height * 0.55 ||
        (velocity.y > velocityForClose && dismissableViaSwipe)
      ) {
        openChange(false)
        return
      }

      // Otherwise animate the dialog back to the starting point.
      animate(dialogContainerRef.current, { y: '0%' }, modalSlideSpring)
    },
    [dialogSize.height, openChange],
  )

  // Ensures that we cancel drag events when dragging on a scrollable.
  const pointerDown = useCallback(
    (e: PointerEvent) => {
      if (!dialogContainerRef.current) {
        return
      }

      if (isDraggingDisabled) {
        return
      }

      const target = e.target as Element
      const scrollingParent = getScrollParent(target, true)
      if (isAncestorElement(dialogContainerRef.current, scrollingParent)) {
        // Cancel drag when we're dragging on a scrollable container that
        // is within the dialog.
        return
      }

      dragControls.start(e)
    },
    [dragControls, isDraggingDisabled],
  )

  // When showing as a sheet, we'll render an overscroll area
  // at the bottom
  const overscrollScale = useTransform(dialogY, (y) => {
    // When dragging up, scale up the overscroll area.
    if (typeof y === 'number' && y < 0) {
      return 1 + Math.abs(y)
    }

    return 0
  })

  const isDraggable = !isFloating && !isDraggingDisabled

  const contextValue = useMemo(
    () => ({
      onClose,
      dragControls,
      isDraggable,
      isFloating,
    }),
    [onClose, dragControls, isDraggable, isFloating],
  )

  return (
    <InternalModalContext.Provider value={contextValue}>
      <ModalOverlay
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled || !isDismissable}
        shouldCloseOnInteractOutside={shouldCloseOnInteractOutsideDefault}
        isOpen={isOpen}
        onOpenChange={openChange}
      >
        {({ isExiting, animate }) => (
          <Modal isExiting={isExiting}>
            <MotionDialog
              data-test={testId}
              {...rest}
              className={cx(
                styles.root,
                isFloating && styles.floating,
                isUsingDialogScrolling && styles.dialogScrolling,
                effectiveSize === 'lg' && styles.lg,
                effectiveSize === 'wide' && styles.wide,
                sheetSizing === 'stretch' && styles.sheetStretch,
              )}
              transition={modalSlideSpring}
              style={{
                transform: stackingTransform,
              }}
            >
              <m.div
                variants={dialogAnimationVariants}
                layoutRoot
                initial={ModalOverlayVariantNames.HIDDEN}
                animate={animate}
                exit={ModalOverlayVariantNames.HIDDEN}
                transition={staticTransition}
                className={cx(styles.dialog)}
                ref={composedDialogContainerRef}
                drag="y"
                dragControls={dragControls}
                dragElastic={{ top: 0.15 }}
                dragConstraints={{ top: 0 }}
                dragMomentum={false}
                // Fixes animations when changing between
                // floating and sheet variants.
                key={isFloating ? 'floating' : 'sheet'}
                onPointerDown={pointerDown}
                dragListener={false}
                onDragEnd={isDraggable ? dragEnd : undefined}
                style={{
                  y: dialogY,
                }}
              >
                {isDraggable && (
                  <div
                    className={styles.dragAffordanceContainer}
                    onPointerDown={(e) => dragControls.start(e)}
                  >
                    <div className={styles.dragAffordance} />
                  </div>
                )}

                {hero}
                <div
                  className={cx(
                    styles.dialogBorder,
                    gap === 'default' && styles.gap,
                  )}
                >
                  {children}
                </div>
              </m.div>
              {!isFloating && (
                <m.div
                  style={{ scaleY: overscrollScale }}
                  className={styles.overscroll}
                />
              )}
            </MotionDialog>
          </Modal>
        )}
      </ModalOverlay>
    </InternalModalContext.Provider>
  )
}

interface ModalDialogHeroProps {
  children?: ReactNode
  isSkeleton?: boolean
  /**
   * Which color variant to use.
   */
  variant?: 'default' | 'success' | 'warning' | 'secondary' | 'background'
  /**
   * Which spacing to use. `compact` removes bottom padding and tightens
   * up top padding.
   */
  spacing?: 'default' | 'compact'
}

/**
 * Dialog Hero
 */
export function ModalDialogHero({
  children,
  variant = 'default',
  spacing = 'default',
  isSkeleton,
}: ModalDialogHeroProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <div
      className={cx(
        styles.hero,
        variant === 'secondary' && styles.heroSecondary,
        variant === 'background' && styles.heroBackground,
        variant === 'success' && styles.heroSuccess,
        variant === 'warning' && styles.heroWarning,
        spacing === 'compact' && styles.heroCompact,
        isSkeleton && styles.heroSkeleton,
      )}
    >
      {children}
    </div>
  )
}

interface ModalDialogSearchHeaderProps {
  value?: string
  onChange?: (text: string) => unknown
  placeholder?: string
  /**
   * Primary action for closing the modal.
   * Should be a {@link Button} with a `variant` of `text`.
   */
  action?: ReactNode
  /**
   * The searchable content. Usually a list box or menu.
   */
  children: ReactElement
  /**
   * Test ID attribute for the search input field.
   */
  inputTestId?: string
  /**
   * Test ID attribute for the clear button.
   */
  clearButtonTestId?: string
}

/**
 * A header for dialogs that provide search functionality.
 */
export function ModalDialogSearchView({
  action,
  value,
  onChange,
  children,
  placeholder = 'Search',
  inputTestId,
  clearButtonTestId,
}: ModalDialogSearchHeaderProps) {
  const { connectRef, scrollYStart } = useScrollOffsets()
  const borderOpacity = useTransform(scrollYStart, [0, 16], ['0%', '100%'])
  const { isFloating } = useContext(InternalModalContext)

  return (
    <div className={styles.searchView}>
      <m.div
        className={styles.searchHeader}
        style={
          {
            '--border-opacity': borderOpacity,
          } as CSSProperties
        }
      >
        <div className={styles.searchContainer}>
          <ModalSearchField
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            autoFocus={isFloating}
            inputTestId={inputTestId}
            clearButtonTestId={clearButtonTestId}
          />
        </div>

        <div className={styles.searchAction}>{action}</div>
      </m.div>
      {/* NOTE: The `children` itself must be scrollable (`overflow-y: auto`) */}
      <div className={styles.searchScroller}>
        {cloneElement(children, {
          // @ts-ignore: just need a ref to an HTML element.
          ref: connectRef,
        })}
      </div>
    </div>
  )
}

interface ModalDialogHeaderProps {
  children?: ReactNode
  textAlign?: 'left' | 'center'
  testId?: string
}

/**
 * Dialog Header
 */
export function ModalDialogHeader({
  children,
  textAlign = 'center',
  testId,
}: ModalDialogHeaderProps) {
  return (
    <header
      data-test={testId}
      className={cx(styles.header, textAlign === 'left' && styles.textLeft)}
    >
      {children}
    </header>
  )
}

interface ModalDialogCompactHeaderProps {
  children?: ReactNode
  'aria-label'?: string
  testId?: string
  closeButtonTestId?: string
}

/**
 * Compact Dialog Header with close button
 */
export function ModalDialogCompactHeader({
  children,
  'aria-label': ariaLabel,
  testId,
  closeButtonTestId,
}: ModalDialogCompactHeaderProps) {
  const { onClose, dragControls, isDraggable } =
    useContext(InternalModalContext)

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (dragControls && isDraggable) {
        dragControls.start(e)
      }
    },
    [dragControls, isDraggable],
  )

  return (
    <header
      data-test={testId}
      className={styles.compactHeader}
      onPointerDown={handlePointerDown}
    >
      <Heading
        slot="title"
        level={1}
        className={styles.compactTitle}
        aria-label={ariaLabel}
      >
        {children}
      </Heading>
      {onClose && (
        <div className={styles.closeButtonContainer}>
          <IconButton
            variant="no-hover"
            label="Close"
            onPress={onClose}
            name="close"
            testId={closeButtonTestId}
          />
        </div>
      )}
    </header>
  )
}

interface ModalDialogContentProps {
  children?: ReactNode

  /**
   * Configure spacing amount.
   */
  spacing?: 'default' | 'compact' | 'bottom' | 'sides' | 'none' | 'hug-top'

  /**
   * Whether to fade the content on scroll.
   *
   * @default 'both'
   */
  fade?: 'both' | 'top' | 'bottom' | 'none'

  /**
   * Whether to allow the content to overscroll.
   */
  isOverscrollDisabled?: boolean

  /**
   * Scroll padding to apply at the top.
   */
  scrollPaddingTop?: number | string

  /**
   * Optional ref to the scrollable container element.
   */
  ref?: Ref<HTMLDivElement>

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Dialog Content
 */
export function ModalDialogContent({
  children,
  spacing = 'default',
  fade = 'both',
  isOverscrollDisabled = false,
  scrollPaddingTop,
  ref,
  testId,
}: ModalDialogContentProps) {
  const { connectRef, scrollYStart, scrollYEnd } = useScrollOffsets()
  const { maskImage } = useScrollFadeMaskImage({
    scrollYStart,
    scrollYEnd,
    fade,
  })

  const composedRef = useComposedRefs(connectRef, ref)

  return (
    <m.div
      data-test={testId}
      className={cx(
        styles.content,
        spacing === 'none' && styles.noSpacing,
        spacing === 'compact' && styles.compactSpacing,
        spacing === 'default' && styles.defaultSpacing,
        spacing === 'bottom' && styles.bottomSpacing,
        spacing === 'sides' && styles.sidesSpacing,
        spacing === 'hug-top' && styles.hugTopSpacing,
        isOverscrollDisabled && styles.noOverscroll,
      )}
      ref={composedRef}
      style={{ maskImage, scrollPaddingTop }}
    >
      <div className={styles.contentInner}>{children}</div>
    </m.div>
  )
}

interface ModalDialogTitleProps {
  children?: ReactNode

  /**
   * Whether to render a skeleton instead of the title.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Dialog Title
 */
export function ModalDialogTitle({
  children,
  isSkeleton,
  testId,
}: ModalDialogTitleProps) {
  return (
    <Heading slot="title" level={1} className={styles.title} data-test={testId}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </Heading>
  )
}

interface ModalDialogDescriptionProps {
  children?: ReactNode
  /**
   * Whether to render a skeleton instead of the description.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Dialog Description
 */
export function ModalDialogDescription({
  children,
  isSkeleton,
  testId,
}: ModalDialogDescriptionProps) {
  return (
    <div data-test={testId} className={styles.description}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

interface ModalDialogDescriptionParagraphProps {
  children?: ReactNode
  /**
   * Whether to render a skeleton instead of the paragraph.
   */
  isSkeleton?: boolean
}

/**
 * A paragraph within a dialog description with proper spacing.
 * Use this for multi-paragraph descriptions with consistent spacing.
 */
export function ModalDialogDescriptionParagraph({
  children,
  isSkeleton,
}: ModalDialogDescriptionParagraphProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <p className={styles.descriptionParagraph}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </p>
  )
}

interface ModalDialogFooterProps {
  children?: ReactNode
  bordered?: boolean
  compact?: boolean
  sticky?: boolean
  testId?: string
}

/**
 * Dialog Footer
 */
export function ModalDialogFooter({
  children,
  bordered,
  compact,
  sticky,
  testId,
}: ModalDialogFooterProps) {
  return (
    <div
      data-test={testId}
      className={cx(
        styles.footer,
        bordered && styles.bordered,
        compact && styles.compact,
        sticky && styles.sticky,
      )}
    >
      {children}
    </div>
  )
}

interface ModalDialogActionsProps {
  children?: ReactNode
  testId?: string
}

/**
 * Dialog Actions
 */
export function ModalDialogActions({
  children,
  testId,
}: ModalDialogActionsProps) {
  return (
    <div data-test={testId} className={cx(styles.actions)}>
      {children}
    </div>
  )
}

// --------------------------------------------------------------------------
// Internal Context
// --------------------------------------------------------------------------

/**
 * Internal context for the modal dialog.
 */
interface InternalModalContextValue {
  onClose?: () => void
  dragControls?: DragControls
  isDraggable?: boolean
  isFloating?: boolean
}

/**
 * Context for the modal dialog.
 */
const InternalModalContext = createContext<InternalModalContextValue>({})
